import { gql, useQuery } from '@apollo/client';
import { ThemeProvider } from '@gasbuddy/react-components';
import { useAnalytics } from '@gasbuddy/react-hooks';
import loadable from '@loadable/component';
import React, { useMemo } from 'react';
import { Route, Switch } from 'react-router-dom';
import { config as FAConfig } from '@fortawesome/fontawesome-svg-core';
import { isWindowAvailable } from '../../../lib/utils';
import logEvent from '../../../lib/utils/logEvent';
import GlobalContext from '../../context/global';
import ErrorBoundary from '../ErrorBoundary';
import PrivateRoute from '../PrivateRoute';

FAConfig.autoAddCss = false;

const LOCALYTICS_CUSTOM_DIMENSIONS = {
  USER_ID: 14,
  USER_NAME: 15,
};

export const GET_PAGES = gql`
  query {
    pages {
      ... on CMSAboutUsPage {
        url
      }
      ... on CMSLandingPage {
        url
      }
    }
  }
`;

export const FETCH_GLOBAL_CONTEXT = gql`
  query fetchGlobalContext {
    config {
      consumerHost
      googleAppId
      identityHost
      imagesHost
      isMobile
      localyticsKey
      payHost
      payNgHost
      publicPath
      recaptchaSiteKey
      sywAppId
      sywHost
      trackerHost
    }
    profile {
      accountId
      memberName
    }
  }
`;

export default function Router() {
  const { data, loading } = useQuery(GET_PAGES);
  const { data: globalContextData } = useQuery(FETCH_GLOBAL_CONTEXT, {
    skip: !isWindowAvailable(), // TODO: this verification should probably be in useAnalytics instead
  });
  const cmsUrls = data?.pages.map(({ url }) => url) || [];
  const { localyticsKey } = globalContextData?.config || {};
  const analytics = useAnalytics({
    localytics: {
      domain: 'gasbuddy.com',
      key: localyticsKey,
    },
  });

  const globalContextValue = useMemo(() => ({
    ...globalContextData,
    analytics,
  }), [globalContextData, analytics]);

  const { accountId, memberName } = globalContextData?.profile || {};

  // Don't allow a landing page to return a 404 on initial render
  if (loading) {
    return null;
  }

  if (analytics) {
    analytics.on('event', (eventName, eventAttributes) => {
      logEvent(`Localytics:${eventName}`, eventAttributes);
    });

    if (accountId) {
      analytics.setCustomDimension(LOCALYTICS_CUSTOM_DIMENSIONS.USER_ID, accountId);
    }

    if (memberName) {
      analytics.identifyUser({
        id: memberName,
      });
      analytics.setCustomDimension(LOCALYTICS_CUSTOM_DIMENSIONS.USER_NAME, memberName);
    }
  }

  return (
    <ThemeProvider>
      <GlobalContext.Provider value={globalContextValue}>
        <ErrorBoundary>
          <Switch>
            {cmsUrls.map(cmsUrl => (
              <Route key={cmsUrl} component={loadable(() => import('../CMSPage'))} exact path={`/${cmsUrl}`} />
            ))}
            <Route exact path="/" component={loadable(() => import('../HomePage'))} />
            <Route exact path="/about/timeline" component={loadable(() => import('../TimelinePage'))} />
            <PrivateRoute exact path="/account/favorite-lists" component={loadable(() => import('../FavoriteStationsListsPage'))} />
            <PrivateRoute exact path="/account/favorite-lists/:favoriteListId" component={loadable(() => import('../FavoritesListPage'))} />
            <PrivateRoute exact path="/account/fuel-log-book" component={loadable(() => import('../RedirectToFuelLogBook'))} />
            <PrivateRoute exact path="/account/preferences" component={loadable(() => import('../UserEmailPreferencesPage'))} />
            <Route exact path="/account/profile" component={loadable(() => import('../ProfilePage'))} />
            <Route exact path="/account/savings" component={loadable(() => import('../PaySavingsPage'))} />
            <PrivateRoute exact path="/account/vehicles" component={loadable(() => import('../MyVehiclesPage'))} />
            <PrivateRoute exact path="/account/vehicles/:guid" component={loadable(() => import('../MyVehicleProfilePage'))} />
            <PrivateRoute exact path="/account/vehicles/:guid/fuel-log-book" component={loadable(() => import('../FuelLogBookPage'))} />
            <PrivateRoute exact path="/account/vehicles/:guid/fuel-log-book/entry/:fuelLogGuid?" component={loadable(() => import('../FuelLogEntryPage'))} />
            <Route exact path="/app" component={loadable(() => import('../AppPage'))} />
            <Route exact path="/brands/:brandKey" component={loadable(() => import('../BrandPage'))} />
            <Route exact path="/contest/gas-card-give-away" component={loadable(() => import('../EnterToWinPage'))} />
            <Route path="/disclaimer/:countryCode?" component={loadable(() => import('../LegalPage'))} />
            <Route exact path="/do-not-sell-my-personal-information" component={loadable(() => import('../DoNotSellMyPI'))} />
            <Route exact path="/emailpreferences" component={loadable(() => import('../VisitorPreferencesPage'))} />
            <Route exact path="/gasback" component={loadable(() => import('../GasbackLanding'))} />
            <Route exact path="/gasprices" component={loadable(() => import('../RegionSelectPage'))} />
            <Route exact path="/gasprices/:region/:area?/:countyId?" component={loadable(() => import('../RegionalGasPrices'))} />
            <Route exact path="/home" component={loadable(() => import('../SearchResultsPage'))} />
            <Route exact path="/newsroom" component={loadable(() => import('../NewsroomPage'))} />
            <Route exact path="/newsroom/inthenews" component={loadable(() => import('../NewsArticlesPage'))} />
            <Route exact path="/newsroom/pressrelease/:legacyId" component={loadable(() => import('../PressReleaseLookup'))} />
            <Route exact path="/newsroom/pressrelease/:year/:month/:day/:identifier" component={loadable(() => import('../PressReleasePage'))} />
            <Route exact path="/newsroom/pressreleases" component={loadable(() => import('../PressReleasesPage'))} />
            <Route exact path="/pay/dynamic/beta" component={loadable(() => import('../DynamicDiscountBetaPage'))} />
            <Route exact path="/pay/dynamic/info" component={loadable(() => import('../DynamicDiscountPage'))} />
            <Route exact path="/pay/plus" component={loadable(() => import('../PayPlusPage'))} />
            <Route exact path="/pay/shop-your-way" component={loadable(() => import('../ShopYourWayPage'))} />
            <Route path="/privacy/:countryCode?" component={loadable(() => import('../LegalPage'))} />
            <Route exact from="/station/:brandName/:regionCode/:regionDescription/:stationId([0-9]+)" to="/station/:stationId" />
            <Route exact path="/station/:stationId([0-9]+)" component={loadable(() => import('../Station'))} />
            <Route path="/terms/:countryCode?" component={loadable(() => import('../LegalPage'))} />
            <Route exact path="/ubi/quote" component={loadable(() => import('../UbiQuote'))} />
            <Route exact path="/vehicles/recalls/results" component={loadable(() => import('../VehicleRecallSearchResultsPage'))} />
            <Route exact path="/vehicles/recalls/search" component={loadable(() => import('../VehicleRecallSearchPage'))} />
            <Route component={loadable(() => import('../Unknown'))} />
          </Switch>
        </ErrorBoundary>
      </GlobalContext.Provider>
    </ThemeProvider>
  );
}
